// Routes: the prototype's hash routes become paths (#today → /today, #settings-general → /settings/general, …).
// The session gate renders sign-in, the lock screen or PIN set-up before any shell route (UC-IDENTITY-001..003).
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router";
import { AuditLog } from "./screens/AuditLog";
import { ControlLog } from "./screens/ControlLog";
import { queryClient } from "./api/query-client";
import { InvitationAccept } from "./screens/InvitationAccept";
import { Lock } from "./screens/Lock";
import { NoAccess, NotFound } from "./screens/NoAccess";
// patients
import { PatientsForm } from "./screens/PatientsForm";
import { PatientsList } from "./screens/PatientsList";
import { RegistrationsQueue } from "./screens/Registrations";
import { PatientsRecord } from "./screens/PatientsRecord";
import { PasswordResetComplete, PasswordResetRequest } from "./screens/PasswordReset";
import { PinSetup } from "./screens/PinSetup";
import { Placeholder, SettingsPlaceholder } from "./screens/Placeholder";
import { SettingsBranches } from "./screens/SettingsBranches";
import { SettingsGeneral } from "./screens/SettingsGeneral";
import { SettingsUsers } from "./screens/SettingsUsers";
import { SignIn } from "./screens/SignIn";
import { Today } from "./screens/Today";
import { useSession } from "./session/store";
import { Shell } from "./shell/Shell";
import { StepUpProvider } from "./stepup/StepUpProvider";
import { ErrorBoundary } from "./ui/ErrorBoundary";
// kiosk, today, appointments
import { KioskConfinement, KioskGate, KioskRoute } from "./kiosk/confinement";
import { Appointments } from "./screens/Appointments";
import { SettingsAppointments } from "./screens/SettingsAppointments";
import { SettingsConsent } from "./screens/SettingsConsent";
// end kiosk, today, appointments
// appointments: recalls (UC-APPOINTMENTS-013)
import { Recalls } from "./screens/Recalls";
// end appointments: recalls
// patients and settings wire-up (D64): the turned-18 worklist (UC-PATIENTS-011) and Settings › Medical aid (UC-PLATFORM-REFERENCE-006)
import { PatientsReconsent } from "./screens/PatientsReconsent";
import { PatientsTurned18 } from "./screens/PatientsTurned18";
// Sprint 2 gaps (8 Oct 2026): Deleted records (UC-PATIENTS-028 A3)
import { PatientsDeleted } from "./screens/PatientsDelete";
import { SettingsMedicalAid } from "./screens/SettingsMedicalAid";
// end patients and settings wire-up
// Sprint 4: the catalogue and the supplier master (UC-CATALOGUE-001..011, UC-PURCHASING-001..004, -006, -007)
import { LensOptions } from "./screens/LensOptions";
import { ProductForm } from "./screens/ProductForm";
import { Products } from "./screens/Products";
import { PurchasingHome, PurchasingLater, Suppliers } from "./screens/Purchasing";
import { ServiceRules } from "./screens/ServiceRules";
import { SettingsCatalogue, SettingsMarkups } from "./screens/SettingsMarkups";
import { SupplierForm } from "./screens/SupplierForm";
import { SupplierRecord } from "./screens/SupplierRecord";
// end Sprint 4: the catalogue and the supplier master
// Sprint 4: the stock screens (UC-INVENTORY-001..012)
import { Stock } from "./screens/Stock";
import { AdjustmentRecord, Adjustments } from "./screens/StockAdjust";
import { StockReview } from "./screens/StockCount";
import { StockMovements } from "./screens/StockMovements";
import { StockSerial } from "./screens/StockSerial";
import { StockTakePage, StockTakeRoute } from "./screens/StockTake";
import { TransferPage, Transfers } from "./screens/StockTransfer";
// end Sprint 4: the stock screens
// Sprint 4: the goods receipts (UC-PURCHASING-005, -008)
import { GoodsReceiptRecord, GoodsReceipts } from "./screens/GoodsReceipts";
import { ReceiveStock } from "./screens/ReceiveStock";
// end Sprint 4: the goods receipts
// Sprint 4: the price-list import (UC-IMPORTS-001..006)
import { PriceImportNew, PriceImportPage, PriceImports } from "./screens/PriceImport";
// end Sprint 4: the price-list import

function RequireSession() {
  const s = useSession();
  const loc = useLocation();
  if (s.status === "loading") return null;
  if (s.status === "anonymous") return <Navigate to="/signin" replace state={{ from: loc.pathname + loc.search }} />;
  if (s.status === "locked") return <Lock />;
  if (s.status === "pin_setup") return <PinSetup />;
  return <Shell />;
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const s = useSession();
  const loc = useLocation();
  if (s.status === "active") {
    const from = (loc.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== "/signin" ? from : "/today"} replace />;
  }
  if (s.status === "locked") return <Lock />;
  if (s.status === "pin_setup") return <PinSetup />;
  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/signin" element={<PublicOnly><SignIn /></PublicOnly>} />
      <Route path="/invitations/:token" element={<InvitationAccept />} />
      <Route path="/password-reset" element={<PasswordResetRequest />} />
      <Route path="/password-reset/:token" element={<PasswordResetComplete />} />
      {/* kiosk, today, appointments: the kiosk renders without the shell (UC-PATIENTS-022 step 4) */}
      <Route path="/kiosk" element={<KioskRoute />} />
      {/* end kiosk, today, appointments */}
      <Route element={<RequireSession />}>
        <Route index element={<Navigate to="/today" replace />} />
        <Route path="/today" element={<Today />} />
        {/* kiosk, today, appointments */}
        <Route path="/appointments" element={<Appointments />} />
        {/* end kiosk, today, appointments */}
        {/* appointments: recalls (UC-APPOINTMENTS-013), reached from Appointments and Today's Needs attention */}
        <Route path="/appointments/recalls" element={<Recalls />} />
        {/* end appointments: recalls */}
        <Route path="/settings" element={<Outlet />}>
          <Route index element={<Navigate to="/settings/general" replace />} />
          <Route path="general" element={<SettingsGeneral />} />
          <Route path="branches" element={<SettingsBranches />} />
          <Route path="branches/:id" element={<SettingsBranches />} />
          <Route path="users" element={<SettingsUsers />} />
          <Route path="users/:id" element={<SettingsUsers />} />
          <Route path="audit-log" element={<AuditLog />} />
          {/* kiosk, today, appointments */}
          <Route path="appointments" element={<SettingsAppointments />} />
          <Route path="consent" element={<SettingsConsent />} />
          {/* end kiosk, today, appointments */}
          {/* patients and settings wire-up (D64) */}
          <Route path="medical-aid" element={<SettingsMedicalAid />} />
          {/* end patients and settings wire-up */}
          {/* Sprint 4: Markup & fitting — the bands, product markups, lens lab lists, pricing settings and recent overrides
              (UC-PRICING-004..007, -009), and its Catalogue settings panel (UC-CATALOGUE-011); the fitting fees wait for Sprint 5 or 7 */}
          <Route path="markups" element={<SettingsMarkups />} />
          <Route path="markups/catalogue" element={<SettingsCatalogue />} />
          {/* end Sprint 4 settings */}
          <Route path=":tab" element={<SettingsPlaceholder />} />
        </Route>
        <Route path="/control-log" element={<ControlLog />} />
        {/* patients */}
        <Route path="/patients" element={<PatientsList />} />
        <Route path="/patients/new" element={<PatientsForm />} />
        <Route path="/patients/registrations" element={<RegistrationsQueue />} />
        {/* patients and settings wire-up (D64): ahead of /patients/:id */}
        <Route path="/patients/turned-18" element={<PatientsTurned18 />} />
        <Route path="/patients/re-consent" element={<PatientsReconsent />} />
        <Route path="/patients/deleted" element={<PatientsDeleted />} />
        {/* end patients and settings wire-up */}
        <Route path="/patients/:id/edit" element={<PatientsForm />} />
        <Route path="/patients/:id/:tab?" element={<PatientsRecord />} />
        {/* Sprint 4: Products (under Stock) and Purchasing › Suppliers; static sub-paths before /:id */}
        <Route path="/products" element={<Products />} />
        <Route path="/products/new" element={<ProductForm />} />
        <Route path="/products/lens-options" element={<LensOptions />} />
        <Route path="/products/combination-rules" element={<ServiceRules />} />
        <Route path="/products/:id/edit" element={<ProductForm />} />
        <Route path="/products/:id" element={<Products />} />
        <Route path="/purchasing" element={<PurchasingHome />} />
        {/* Sprint 4: the goods receipts — static sub-paths before /:id and /purchasing/:tab */}
        <Route path="/purchasing/receipts" element={<GoodsReceipts />} />
        <Route path="/purchasing/receipts/new" element={<ReceiveStock />} />
        <Route path="/purchasing/receipts/:id" element={<GoodsReceiptRecord />} />
        {/* end Sprint 4: the goods receipts */}
        <Route path="/purchasing/suppliers" element={<Suppliers />} />
        <Route path="/purchasing/suppliers/new" element={<SupplierForm />} />
        <Route path="/purchasing/suppliers/:id/edit" element={<SupplierForm />} />
        <Route path="/purchasing/suppliers/:id/:tab?" element={<SupplierRecord />} />
        <Route path="/purchasing/:tab" element={<PurchasingLater />} />
        {/* end Sprint 4 */}
        {/* Sprint 4: the stock screens — static sub-paths before /:id */}
        <Route path="/stock" element={<Stock />} />
        <Route path="/stock/movements" element={<StockMovements />} />
        <Route path="/stock/adjustments" element={<Adjustments />} />
        <Route path="/stock/adjustments/:id" element={<AdjustmentRecord />} />
        <Route path="/stock/serials/:id" element={<StockSerial />} />
        <Route path="/stock/receive" element={<ReceiveStock />} />
        <Route path="/stock-take" element={<StockTakePage />} />
        <Route path="/stock-take/:id" element={<StockTakeRoute />} />
        <Route path="/stock-take/:id/review" element={<StockReview />} />
        <Route path="/stock-transfer" element={<Transfers />} />
        <Route path="/stock-transfer/new" element={<TransferPage />} />
        <Route path="/stock-transfer/:id" element={<TransferPage />} />
        {/* end Sprint 4: the stock screens */}
        {/* Sprint 4: the price-list import, under Stock — /price-import/new before /:id */}
        <Route path="/price-import" element={<PriceImports />} />
        <Route path="/price-import/new" element={<PriceImportNew />} />
        <Route path="/price-import/:id" element={<PriceImportPage />} />
        {/* end Sprint 4: the price-list import */}
        <Route path="/no-access" element={<NoAccess />} />
        <Route path="/:section" element={<Placeholder />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    // The last resort, for a failure outside a routed screen (the shell, the kiosk, the providers): never a blank page.
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <StepUpProvider>
          {/* kiosk, today, appointments: in kiosk mode every other route comes back to /kiosk (BR-PATIENTS-014) */}
          <KioskConfinement />
          {/* end kiosk, today, appointments */}
          <KioskGate><AppRoutes /></KioskGate>
        </StepUpProvider>
      </BrowserRouter>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}
